import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import { PageHeader, SimpleSelect } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, Tags, Upload, FileSpreadsheet, Loader2, Download, CheckCircle2, AlertTriangle } from "lucide-react";

export default function Poliklinik() {
  const { isSuperAdmin } = useAuth();
  const { indikators } = useMeta();
  const [list, setList] = useState([]);
  const [filterInd, setFilterInd] = useState("__all__");
  const [inds, setInds] = useState([]);
  const [form, setForm] = useState({ nama_departemen: "", nama_poliklinik: "", indikator: "" });
  const [editId, setEditId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [indDialog, setIndDialog] = useState(false);
  const [indForm, setIndForm] = useState({ kode: "", nama: "" });
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const bulkFileRef = useRef();

  const loadInds = () => api.get("/indikator").then((r) => setInds(r.data));
  const load = () => {
    let url = "/poliklinik";
    if (filterInd !== "__all__") url += `?indikator=${encodeURIComponent(filterInd)}`;
    api.get(url).then((r) => setList(r.data));
  };

  useEffect(() => { loadInds(); }, []);
  useEffect(() => { load(); }, [filterInd]);
  useEffect(() => {
    if (!form.indikator && inds.length) setForm((f) => ({ ...f, indikator: inds[0].kode }));
  }, [inds]);

  const openAdd = () => {
    setEditId(null);
    setForm({ nama_departemen: "", nama_poliklinik: "", indikator: inds[0]?.kode || "" });
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditId(p.id);
    setForm({ nama_departemen: p.nama_departemen, nama_poliklinik: p.nama_poliklinik, indikator: p.indikator });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.nama_departemen || !form.nama_poliklinik || !form.indikator) return toast.error("Lengkapi semua field");
    try {
      if (editId) await api.put(`/poliklinik/${editId}`, form);
      else await api.post("/poliklinik", form);
      toast.success("Data poliklinik disimpan");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/poliklinik/${id}`);
      toast.success("Poliklinik dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const saveInd = async () => {
    if (!indForm.kode) return toast.error("Kode indikator wajib diisi");
    try {
      await api.post("/indikator", indForm);
      toast.success("Indikator ditambahkan");
      setIndForm({ kode: "", nama: "" });
      loadInds();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };
  const delInd = async (id) => {
    await api.delete(`/indikator/${id}`);
    toast.success("Indikator dihapus");
    loadInds();
  };

  const downloadBulkTemplate = () => {
    const csv = "Departemen,Poliklinik,Indikator\nPenyakit Dalam,Poli Endokrin,WTRJ\nBedah,Poli Bedah Urologi,WPRJ TPP IPRJT\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "template_master_poliklinik.csv";
    a.click();
  };

  const openBulk = () => {
    setBulkFile(null);
    setBulkPreview(null);
    if (bulkFileRef.current) bulkFileRef.current.value = "";
    setBulkDialog(true);
  };

  const doBulkPreview = async () => {
    if (!bulkFile) return toast.error("Pilih file terlebih dahulu");
    setBulkLoading(true);
    setBulkPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      const res = await api.post("/poliklinik/bulk/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkPreview(res.data);
      toast.success(`${res.data.jumlah_baru} baris baru siap ditambahkan`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBulkLoading(false);
    }
  };

  const doBulkConfirm = async () => {
    if (!bulkPreview || bulkPreview.baru.length === 0) return;
    setBulkSaving(true);
    try {
      const res = await api.post("/poliklinik/bulk/confirm", { rows: bulkPreview.baru });
      toast.success(res.data.message);
      setBulkDialog(false);
      setBulkPreview(null);
      setBulkFile(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div data-testid="poliklinik-page">
      <PageHeader icon={Building2} title="Kelola Master Poliklinik" subtitle="Data referensi departemen, poliklinik, dan indikator. Satu poliklinik bisa punya beberapa indikator.">
        <Dialog open={indDialog} onOpenChange={setIndDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="manage-indikator-button"><Tags className="w-4 h-4 mr-2" /> Kelola Indikator</Button>
          </DialogTrigger>
          <DialogContent data-testid="indikator-dialog">
            <DialogHeader>
              <DialogTitle>Kelola Indikator Mutu</DialogTitle>
              <DialogDescription>Tambah indikator baru kapan saja tanpa bantuan developer.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {inds.map((i) => (
                <div key={i.id} className="flex items-center justify-between border rounded-lg px-3 py-2" data-testid={`indikator-item-${i.kode}`}>
                  <div><div className="font-semibold text-slate-800">{i.kode}</div><div className="text-xs text-slate-500">{i.nama}</div></div>
                  {isSuperAdmin && (
                    <Button size="icon" variant="ghost" onClick={() => delInd(i.id)} data-testid={`delete-indikator-${i.kode}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                  )}
                </div>
              ))}
              <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                <div className="space-y-1"><Label>Kode</Label><Input value={indForm.kode} onChange={(e) => setIndForm({ ...indForm, kode: e.target.value })} placeholder="Mis. WTRJ" data-testid="new-indikator-kode" /></div>
                <div className="space-y-1"><Label>Nama</Label><Input value={indForm.nama} onChange={(e) => setIndForm({ ...indForm, nama: e.target.value })} placeholder="Nama lengkap" data-testid="new-indikator-nama" /></div>
              </div>
              <Button onClick={saveInd} className="w-full" data-testid="save-indikator-button"><Plus className="w-4 h-4 mr-2" /> Tambah Indikator</Button>
            </div>
          </DialogContent>
        </Dialog>
        <Button variant="outline" onClick={openBulk} data-testid="bulk-upload-button">
          <Upload className="w-4 h-4 mr-2" /> Upload Massal
        </Button>
        <Button onClick={openAdd} data-testid="poliklinik-add-button">
          <Plus className="w-4 h-4 mr-2" /> Tambah Poliklinik
        </Button>
      </PageHeader>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-lg">Daftar Poliklinik ({list.length})</CardTitle>
          <SimpleSelect testId="poli-filter-indikator" value={filterInd} onChange={setFilterInd}
            options={[{ label: "Semua Indikator", value: "__all__" }, ...indikators.map((i) => ({ label: i.kode, value: i.kode }))]}
            className="w-[200px]" />
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Departemen</TableHead>
                  <TableHead>Poliklinik</TableHead>
                  <TableHead>Indikator</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((p) => (
                  <TableRow key={p.id} data-testid={`poli-row-${p.id}`}>
                    <TableCell className="text-slate-600">{p.nama_departemen}</TableCell>
                    <TableCell className="font-medium">{p.nama_poliklinik}</TableCell>
                    <TableCell><Badge variant="secondary">{p.indikator}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-poli-${p.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                      {isSuperAdmin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" data-testid={`delete-poli-${p.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus poliklinik ini?</AlertDialogTitle>
                              <AlertDialogDescription>Data target & capaian terkait juga akan terhapus. Tindakan ini tidak bisa dibatalkan.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del(p.id)} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-poli-${p.id}`}>Hapus</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {list.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">Belum ada data poliklinik</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="poliklinik-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Poliklinik" : "Tambah Poliklinik"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1"><Label>Departemen</Label>
              <Input value={form.nama_departemen} onChange={(e) => setForm({ ...form, nama_departemen: e.target.value })} placeholder="Mis. Penyakit Dalam" data-testid="poli-departemen-input" /></div>
            <div className="space-y-1"><Label>Nama Poliklinik</Label>
              <Input value={form.nama_poliklinik} onChange={(e) => setForm({ ...form, nama_poliklinik: e.target.value })} placeholder="Mis. Poli Endokrin" data-testid="poli-nama-input" /></div>
            <div className="space-y-1"><Label>Indikator</Label>
              <SimpleSelect testId="poli-indikator-select" value={form.indikator} onChange={(v) => setForm({ ...form, indikator: v })}
                options={inds.map((i) => ({ label: i.kode, value: i.kode }))} placeholder="Pilih indikator" className="w-full" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="poli-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle>Upload Massal Poliklinik</DialogTitle>
            <DialogDescription>
              File Excel/CSV dengan kolom <b>Departemen | Poliklinik | Indikator</b>. Baris yang sudah ada akan ditandai duplikat dan dilewati otomatis.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={downloadBulkTemplate} data-testid="bulk-template-button">
                <Download className="w-4 h-4 mr-2" /> Unduh Template CSV
              </Button>
            </div>

            <div
              onClick={() => bulkFileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setBulkFile(e.dataTransfer.files[0]); }}
              data-testid="bulk-dropzone"
              className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-brand-sky hover:bg-brand-mist transition-colors"
            >
              <FileSpreadsheet className="w-9 h-9 mx-auto text-slate-400 mb-2" />
              {bulkFile ? <p className="text-slate-700 font-medium">{bulkFile.name}</p>
                : <p className="text-slate-600">Klik atau seret file .xlsx / .csv ke sini</p>}
              <input ref={bulkFileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                data-testid="bulk-file-input" onChange={(e) => setBulkFile(e.target.files[0])} />
            </div>

            {!bulkPreview && (
              <div className="flex justify-end">
                <Button onClick={doBulkPreview} disabled={bulkLoading || !bulkFile} data-testid="bulk-preview-button">
                  {bulkLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Pratinjau
                </Button>
              </div>
            )}

            {bulkPreview && (
              <div className="space-y-4" data-testid="bulk-preview">
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                    <div className="text-2xl font-extrabold text-emerald-700 tabular-nums" data-testid="bulk-baru-count">{bulkPreview.jumlah_baru}</div>
                    <div className="text-xs text-emerald-600">Baris baru</div>
                  </div>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
                    <div className="text-2xl font-extrabold text-amber-700 tabular-nums" data-testid="bulk-duplikat-count">{bulkPreview.jumlah_duplikat}</div>
                    <div className="text-xs text-amber-600">Duplikat (dilewati)</div>
                  </div>
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-center">
                    <div className="text-2xl font-extrabold text-rose-700 tabular-nums" data-testid="bulk-gagal-count">{bulkPreview.jumlah_gagal}</div>
                    <div className="text-xs text-rose-600">Indikator tidak dikenali</div>
                  </div>
                </div>

                {bulkPreview.jumlah_gagal > 0 && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 flex gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>Baris dengan indikator tak dikenali tidak akan disimpan. Tambahkan indikatornya dulu via "Kelola Indikator", lalu upload ulang.</div>
                  </div>
                )}

                <div className="overflow-x-auto rounded-lg border border-slate-200 max-h-72 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 sticky top-0">
                        <TableHead>Departemen</TableHead>
                        <TableHead>Poliklinik</TableHead>
                        <TableHead>Indikator</TableHead>
                        <TableHead className="text-right">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bulkPreview.baru.map((r, i) => (
                        <TableRow key={`b${i}`} data-testid={`bulk-row-baru-${i}`}>
                          <TableCell className="text-slate-600">{r.nama_departemen}</TableCell>
                          <TableCell className="font-medium">{r.nama_poliklinik}</TableCell>
                          <TableCell><Badge variant="secondary">{r.indikator}</Badge></TableCell>
                          <TableCell className="text-right"><Badge className="bg-emerald-100 text-emerald-800">Baru</Badge></TableCell>
                        </TableRow>
                      ))}
                      {bulkPreview.duplikat.map((r, i) => (
                        <TableRow key={`d${i}`} className="opacity-60">
                          <TableCell className="text-slate-600">{r.nama_departemen}</TableCell>
                          <TableCell className="font-medium">{r.nama_poliklinik}</TableCell>
                          <TableCell><Badge variant="secondary">{r.indikator}</Badge></TableCell>
                          <TableCell className="text-right"><Badge className="bg-amber-100 text-amber-800">Duplikat</Badge></TableCell>
                        </TableRow>
                      ))}
                      {bulkPreview.gagal.map((r, i) => (
                        <TableRow key={`g${i}`} className="opacity-70">
                          <TableCell className="text-slate-600">{r.nama_departemen}</TableCell>
                          <TableCell className="font-medium">{r.nama_poliklinik}</TableCell>
                          <TableCell className="text-rose-600">{r.indikator}</TableCell>
                          <TableCell className="text-right"><Badge className="bg-rose-100 text-rose-800">Gagal</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog(false)}>Tutup</Button>
            {bulkPreview && (
              <Button onClick={doBulkConfirm} disabled={bulkSaving || bulkPreview.jumlah_baru === 0} data-testid="bulk-confirm-button" className="bg-emerald-600 hover:bg-emerald-700">
                {bulkSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Simpan {bulkPreview.jumlah_baru} Poliklinik
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
